
import React, { useEffect, useState } from 'react';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCategoriesAndTrades, CategoryOrTrade } from './hooks/useCategoriesAndTrades';

interface CategoriesAndTradesFieldsProps {
  form: any;
  selectedCategory: CategoryOrTrade | null;
  setSelectedCategory: React.Dispatch<React.SetStateAction<CategoryOrTrade | null>>;
  selectedTrade: CategoryOrTrade | null;
  setSelectedTrade: React.Dispatch<React.SetStateAction<CategoryOrTrade | null>>;
}

const CategoriesAndTradesFields: React.FC<CategoriesAndTradesFieldsProps> = ({
  form,
  selectedCategory,
  setSelectedCategory,
  selectedTrade,
  setSelectedTrade
}) => {
  const { categories = [], trades = [], isLoading, hasCompany } = useCategoriesAndTrades();
  const [categoriesReady, setCategoriesReady] = useState(false);
  const [tradesReady, setTradesReady] = useState(false);

  // Get stored category and trade values from form
  const categoryValue = form.watch('category');
  const tradeValue = form.watch('trade');

  // Set selected category and trade objects when categories and trades are loaded
  useEffect(() => {
    if (categories.length > 0) {
      if (categoryValue && !selectedCategory) {
        const matchedCategory = categories.find(c => c && c.name === categoryValue);
        if (matchedCategory) {
          setSelectedCategory(matchedCategory);
          // Ensure category_id is properly set in the form
          form.setValue('category_id', matchedCategory.id);
        }
      }
      setCategoriesReady(true);
    }
  }, [categories, categoryValue, selectedCategory, setSelectedCategory, form]);

  // Set selected trade when trades are loaded
  useEffect(() => {
    if (trades.length > 0) {
      if (tradeValue && !selectedTrade) {
        const matchedTrade = trades.find(t => t && t.name === tradeValue);
        if (matchedTrade) {
          setSelectedTrade(matchedTrade);
        }
      }
      setTradesReady(true);
    }
  }, [trades, tradeValue, selectedTrade, setSelectedTrade]);

  // Handle category selection
  const handleCategoryChange = (value: string) => {
    if (!value || value === "_none") {
      setSelectedCategory(null);
      form.setValue('category', null);
      form.setValue('category_id', null);
      
      // Reset to global pricing if no category and no custom tiers
      const useCustomTiers = form.getValues('use_custom_tiers');
      if (!useCustomTiers) {
        form.setValue('pricing_source', 'global');
      }
      
      return;
    }
    
    const category = categories.find(c => c && c.id === value);
    if (category) {
      setSelectedCategory(category);
      form.setValue('category', category.name);
      form.setValue('category_id', category.id);
      
      // When changing category, update pricing source unless custom tiers are enabled
      const useCustomTiers = form.getValues('use_custom_tiers');
      if (!useCustomTiers) {
        form.setValue('pricing_source', 'category');
      }
    }
  };

  // Handle trade selection
  const handleTradeChange = (value: string) => {
    if (!value || value === "_none") {
      setSelectedTrade(null);
      form.setValue('trade', null);
      return;
    }
    
    const trade = trades.find(t => t && t.id === value);
    if (trade) {
      setSelectedTrade(trade);
      form.setValue('trade', trade.name);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading categories and trades...</div>;
  }

  if (!hasCompany) {
    return <div className="text-sm text-muted-foreground">Company profile is required to manage categories and trades.</div>;
  }

  // Filter out any invalid categories or trades (ones without IDs)
  const validCategories = categories.filter(category => category && category.id);
  const validTrades = trades.filter(trade => trade && trade.id);

  return (
    <div className="space-y-6">
      <div className="mb-2 text-sm text-muted-foreground">
        Select a category and/or a trade for this line item. Categories with markup tiers will be used for pricing calculations.
      </div>
      
      {/* Category Selection */}
      <FormField
        control={form.control}
        name="category"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Category</FormLabel>
            <Select
              value={selectedCategory?.id || ""}
              onValueChange={handleCategoryChange}
              disabled={!categoriesReady}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a category">
                  {selectedCategory?.name || "Select a category"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">No Category</SelectItem>
                {validCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      
      {/* Trade Selection */}
      <FormField
        control={form.control}
        name="trade"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Trade</FormLabel>
            <Select
              value={selectedTrade?.id || ""}
              onValueChange={handleTradeChange}
              disabled={!tradesReady}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a trade">
                  {selectedTrade?.name || "Select a trade"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">No Trade</SelectItem>
                {validTrades.map((trade) => (
                  <SelectItem key={trade.id} value={trade.id}>
                    {trade.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default CategoriesAndTradesFields;
